import { useCallback, useEffect, useRef, useState } from 'react'
import { BRAND } from '../brand'
import { cardAltText, SHARE_TITLE, shareText } from '../card/copy'
import type { CardSizeId } from '../card/layout'
import { SHAREABLE_SIZES } from '../card/layout'
import type { CardModel } from '../card/model'
import { ensureCardFonts, renderCardToBlob } from '../card/render'

/**
 * The share flow: render both cards, then hand the chosen one to the OS.
 *
 * The two-step UI — tap share, pick Post or Story, then the native sheet — is a
 * product decision that turns out to solve a technical problem for free. iOS
 * Safari requires `navigator.share()` to be called with **transient user
 * activation**, which an `await` inside the click handler can consume: waiting on
 * fonts and `toBlob` before calling `share()` risks the browser deciding the call
 * is no longer a response to a gesture. Because the first tap opens the sheet,
 * both blobs are already rendered by the time the second tap fires, and `share()`
 * runs synchronously inside that gesture with a `File` in hand.
 */

export type ShareStatus = 'idle' | 'preparing' | 'ready' | 'error'

export interface ShareCardState {
  status: ShareStatus
  /** Object URLs for the rendered previews, keyed by size. */
  previews: Partial<Record<CardSizeId, string>>
  error: string | null
  /** Disclosure of something the share did that the user did not see. */
  notice: string | null
  altText: string
  open: () => void
  close: () => void
  isOpen: boolean
  share: (size: CardSizeId) => void
  /**
   * Write the card to the device, without going near the share sheet.
   *
   * Not merely a fallback — a first-class action. Handing an image to the OS
   * share sheet reaches Stories well, but posting to a FEED generally means
   * having the file in the camera roll first and composing the post in the app.
   * Leaving that path to be discovered by long-pressing a thumbnail hides the
   * only route that reliably reaches the surface the 4:5 card is cut for.
   */
  save: (size: CardSizeId) => void
}

// The offered sizes come from SHAREABLE_SIZES, not a second list. A local copy
// here was the other half of the split-brain that constant was created to end:
// the sheet drew buttons from one list while this rendered cards from another.

/**
 * @param buildModel Called at the moment the sheet opens, never during render.
 *
 * That timing is the whole point. An earlier version took a `CardModel` built in
 * the caller's render, which meant the card was drawn from whatever was true at
 * the *previous* render — the app keeps its settled result in a ref, so the model
 * lagged a run behind, and the theme was read during a render that a theme toggle
 * never triggers. Both produced a card that disagreed with the screen it was
 * shared from. Reading everything inside the tap makes those states unreachable.
 */
export function useShareCard(buildModel: () => CardModel | null): ShareCardState {
  const [isOpen, setIsOpen] = useState(false)
  const [status, setStatus] = useState<ShareStatus>('idle')
  const [previews, setPreviews] = useState<Partial<Record<CardSizeId, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [model, setModel] = useState<CardModel | null>(null)
  /** A one-line result of the last action, when the user needs telling. */
  const [notice, setNotice] = useState<string | null>(null)

  const blobsRef = useRef<Partial<Record<CardSizeId, Blob>>>({})
  const urlsRef = useRef<string[]>([])
  /** Monotonic, so a superseded render cannot report over a newer one. */
  const runRef = useRef(0)

  // Warm the faces while the browser is idle, so the first share is not also the
  // first time the card's fonts are asked for.
  useEffect(() => {
    const warm = () => void ensureCardFonts()
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(warm)
      return () => cancelIdleCallback(id)
    }
    const id = setTimeout(warm, 1200)
    return () => clearTimeout(id)
  }, [])

  const revokeAll = useCallback(() => {
    for (const url of urlsRef.current) URL.revokeObjectURL(url)
    urlsRef.current = []
    blobsRef.current = {}
    setPreviews({})
  }, [])

  useEffect(() => revokeAll, [revokeAll])

  const open = useCallback(() => {
    const next = buildModel()
    if (next === null) return

    // A generation counter, not a closure flag. `open` is a click handler, so a
    // cleanup function returned from it is never called by anything — the
    // previous version returned one and its `cancelled` guard was dead code, and
    // a second tap during a slow render could let the first run write its
    // previews and status over the second's.
    const run = runRef.current + 1
    runRef.current = run

    setModel(next)
    setIsOpen(true)
    setError(null)
    setNotice(null)
    setStatus('preparing')
    revokeAll()

    void (async () => {
      try {
        // Once for the whole batch. `renderCardToBlob` awaits the fonts too, but
        // the promise is shared, so this does not pay the wait twice.
        await ensureCardFonts()
        const rendered = await Promise.all(
          SHAREABLE_SIZES.map(
            async (size) => [size, await renderCardToBlob(next, size)] as const,
          ),
        )
        if (runRef.current !== run) return

        const nextPreviews: Partial<Record<CardSizeId, string>> = {}
        for (const [size, blob] of rendered) {
          blobsRef.current[size] = blob
          const url = URL.createObjectURL(blob)
          urlsRef.current.push(url)
          nextPreviews[size] = url
        }
        setPreviews(nextPreviews)
        setStatus('ready')
      } catch (cause) {
        if (runRef.current !== run) return
        setError(cause instanceof Error ? cause.message : 'The card could not be drawn.')
        setStatus('error')
      }
    })()
  }, [buildModel, revokeAll])

  const close = useCallback(() => {
    // Bump the generation so an in-flight render cannot report into a dismissed
    // sheet, and release the blobs now rather than at the next open. Two
    // full-size PNGs is several megabytes; holding them for the rest of the
    // session because someone changed their mind is the same leak `save` was
    // just fixed for, one level up.
    runRef.current += 1
    revokeAll()
    setIsOpen(false)
    setStatus('idle')
  }, [revokeAll])

  /** Download the PNG and put the bare link on the clipboard. See `save` on the state type. */
  const save = useCallback(
    (size: CardSizeId) => {
      // Reuse the preview's object URL rather than minting a second one for the
      // same multi-megabyte blob; every tap used to leak another copy that lived
      // until the sheet was reopened.
      const url = previews[size]
      if (url === undefined) return

      const link = document.createElement('a')
      link.href = url
      link.download = `glidepath-${size}.png`
      link.click()

      // Writing to someone's clipboard silently destroys whatever they had
      // copied, so say that it happened. `notice` is rendered by the sheet.
      void navigator.clipboard
        ?.writeText(BRAND.url)
        .then(() => setNotice('Card saved, and the link is on your clipboard.'))
        .catch(() =>
          setNotice('Card saved. Copy the link from the address bar to post with it.'),
        )
    },
    [previews],
  )

  /**
   * Hand the card to the OS share sheet, and fall back to saving it.
   *
   * Feature-detected rather than sniffed, and deliberately NOT falling back to a
   * text-only share: the user asked for an image, and a sheet that quietly posts
   * a bare link instead is worse than one that saves the file. The rendered
   * `<img>` also stays on screen throughout, because in-app browsers can lack
   * `navigator.share` AND `<a download>` with no detectable signal, and a
   * long-press save is the one path that cannot dead-end.
   *
   * Note this is a share *intent*: it names no app. The OS decides what appears
   * in the sheet, and the user picks. Nothing here opens Instagram directly.
   */
  const share = useCallback(
    (size: CardSizeId) => {
      const blob = blobsRef.current[size]
      if (blob === undefined || model === null) return

      const file = new File([blob], `glidepath-${size}.png`, { type: 'image/png' })

      if (
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })
      ) {
        navigator
          .share({ files: [file], title: SHARE_TITLE, text: shareText(model) })
          .catch((cause: unknown) => {
            // Dismissing the OS sheet rejects with AbortError. That is a normal
            // outcome, not a failure worth showing an error for.
            if (cause instanceof Error && cause.name === 'AbortError') return
            setError('Sharing was not possible. Press and hold the image to save it.')
            setStatus('error')
          })
        return
      }

      // No file sharing here — in-app browsers are the usual reason — so do the
      // thing the user asked for by the other route rather than dead-ending.
      save(size)
    },
    [model, save],
  )

  return {
    status,
    previews,
    error,
    notice,
    altText: model === null ? '' : cardAltText(model),
    open,
    close,
    isOpen,
    share,
    save,
  }
}
